import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PREVIEW_IMG = "/founder/founder-park.jpeg";
const COVER = "/product/cover.png";
const FREE_PDF = "/Working-Mom-Weekly-Reset-Free-Preview.pdf";

const previewIncludes = [
  "The full RESET Method™ overview and how to use it",
  "The 'Reflect' section — honor the week you just lived",
  "Your first guided 'Looking Back' worksheet to fill in",
];

export default function FreePreview() {
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/subscribe`, { ...form, source: "free_preview" });
      setDone(true);
    } catch (err) {
      setSending(false);
    }
  };

  return (
    <main data-testid="free-preview-page" className="min-h-screen">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {/* LEFT — image */}
        <div className="relative hidden lg:block overflow-hidden bg-charcoal">
          <img src={PREVIEW_IMG} alt="Jenna Martins" className="absolute inset-0 w-full h-full object-cover object-[35%_center]" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/25 to-charcoal/40" />
          <div className="absolute top-0 left-0 p-12 xl:p-16 pt-28 xl:pt-32">
            <p className="text-xs uppercase tracking-[0.3em] text-blush mb-4">Free Preview</p>
            <p className="font-serif font-light text-cream text-3xl xl:text-4xl leading-snug max-w-md">
              Start with five minutes and one honest week.
            </p>
          </div>
          <motion.img
            src={COVER}
            alt="The Working Mom Weekly Reset workbook"
            initial={{ opacity: 0, y: 30, rotate: -5 }}
            animate={{ opacity: 1, y: 0, rotate: -5 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="absolute bottom-10 right-10 w-[46%] shadow-2xl shadow-black/50 border border-cream/20"
          />
        </div>

        {/* RIGHT — form / success */}
        <div className="flex items-center justify-center px-6 md:px-12 pt-32 pb-24 lg:py-16 bg-cream">
          <div className="w-full max-w-md">
            <AnimatePresence mode="wait">
              {!done ? (
                <motion.div
                  key="form"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.6 }}
                >
                  <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-6">A gift, on us</p>
                  <h1 className="font-serif font-light text-wine text-4xl md:text-5xl leading-tight">
                    Try a free preview of the Weekly Reset™
                  </h1>
                  <p className="mt-6 text-charcoal/75 leading-relaxed">
                    Enter your email and we'll send you a free excerpt — a taste of the practice that helps
                    ambitious mothers lead the week instead of reacting to it.
                  </p>

                  <ul className="mt-8 space-y-3">
                    {previewIncludes.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-charcoal/80">
                        <Check size={16} className="mt-0.5 text-terracotta flex-none" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <form onSubmit={submit} className="mt-10 space-y-6" data-testid="preview-form">
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] text-charcoal/60">First name</label>
                      <input
                        value={form.first_name}
                        onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                        data-testid="preview-name-input"
                        className="w-full bg-transparent border-b-2 border-gold/50 py-3 mt-2 text-charcoal focus:outline-none focus:border-terracotta transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] text-charcoal/60">Email address</label>
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="preview-email-input"
                        className="w-full bg-transparent border-b-2 border-gold/50 py-3 mt-2 text-charcoal focus:outline-none focus:border-terracotta transition-colors"
                      />
                    </div>
                    <motion.button
                      whileTap={{ scale: 0.97 }}
                      type="submit"
                      disabled={sending}
                      data-testid="preview-submit-button"
                      className="group w-full inline-flex items-center justify-center gap-3 rounded-full bg-terracotta text-cream px-8 py-4 text-sm font-medium tracking-wide hover:bg-wine transition-colors duration-300 disabled:opacity-60"
                    >
                      {sending ? "Sending…" : "Send Me the Free Preview"}
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </motion.button>
                    <p className="text-xs text-charcoal/50 text-center">
                      No spam, ever. Unsubscribe anytime.
                    </p>
                  </form>
                </motion.div>
              ) : (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                  data-testid="preview-success"
                >
                  <div className="w-16 h-16 rounded-full bg-terracotta text-cream flex items-center justify-center">
                    <Check size={30} />
                  </div>
                  <h1 className="font-serif font-light text-wine text-4xl md:text-5xl leading-tight mt-8">
                    {form.first_name ? `Thank you, ${form.first_name}.` : "Thank you."}
                  </h1>
                  <p className="mt-6 text-charcoal/75 leading-relaxed">
                    Your free preview is on its way to <span className="text-wine font-medium">{form.email}</span>.
                    While you wait, download your excerpt below and take five minutes for your first reset.
                  </p>
                  <a
                    href={FREE_PDF}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="preview-download-button"
                    className="group inline-flex items-center gap-3 mt-10 rounded-full bg-terracotta text-cream px-8 py-4 text-sm font-medium tracking-wide hover:bg-wine transition-colors duration-300"
                  >
                    <Download size={18} /> Download the Free Preview (PDF)
                  </a>
                  <p className="mt-8 text-sm text-charcoal/70">
                    Ready for the full practice?{" "}
                    <a href="/shop" className="link-underline text-terracotta font-medium">Get the Weekly Reset™ for $17</a>.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </main>
  );
}
